/**
 * PawPlan Bundle Results Page JavaScript
 * Handles product loading, Rebuy integration, subscription options, and bundle cart functionality
 */

(function () {
  'use strict';

  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  const REBUY_DATASOURCE_ID = '299633';
  const REBUY_API_KEY = '7743237534abf95e65683099a7c802b7b800da04';

  // AI Worker proxy URL (Cloudflare Worker)
  const AI_WORKER_URL = 'https://pawplan-ai-proxy.ewan-7c3.workers.dev';

  // Category display map — maps product_type values to friendly display labels
  const CATEGORY_LABELS = {
    'flea': 'Flea & Worm Treatment',
    'worm': 'Flea & Worm Treatment',
    'parasite': 'Flea & Worm Treatment',
    'treatment': 'Flea & Worm Treatment',
    'tick': 'Flea & Worm Treatment',
    'food': 'Nutrition & Treats',
    'treat': 'Nutrition & Treats',
    'nutrition': 'Nutrition & Treats',
    'freeze': 'Nutrition & Treats',
    'dental': 'Dental & Health',
    'health': 'Dental & Health',
    'calm': 'Dental & Health',
    'supplement': 'Dental & Health',
    'grooming': 'Grooming & Accessories',
    'accessory': 'Grooming & Accessories',
    'bag': 'Grooming & Accessories',
    'waste': 'Grooming & Accessories',
  };

  const CATEGORY_ORDER = [
    'Flea & Worm Treatment',
    'Nutrition & Treats',
    'Dental & Health',
    'Grooming & Accessories',
    'Other',
  ];

  // Store product data for cart operations
  let bundleProducts = [];

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  function getCookie(name) {
    const cname = `${name}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i].trim();
      if (c.indexOf(cname) === 0) return c.substring(cname.length);
    }
    return '';
  }

  function getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function showToast(message, duration = 3000) {
    let toast = document.querySelector('.pawplan-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'pawplan-toast';
      toast.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <span class="pawplan-toast__text"></span>
      `;
      document.body.appendChild(toast);
    }

    toast.querySelector('.pawplan-toast__text').textContent = message;

    setTimeout(() => toast.classList.add('pawplan-toast--visible'), 10);

    setTimeout(() => {
      toast.classList.remove('pawplan-toast--visible');
    }, duration);
  }

  // ============================================================================
  // PET DATA MANAGEMENT
  // ============================================================================

  function getPetInfo() {
    const petsInfoCookie = getCookie('pets_info');
    if (!petsInfoCookie) return null;

    try {
      const petsInfo = JSON.parse(petsInfoCookie);
      return petsInfo && petsInfo.length > 0 ? petsInfo[petsInfo.length - 1] : null;
    } catch (e) {
      console.error('Error parsing pet info:', e);
      return null;
    }
  }

  function getPetName() {
    const petInfo = getPetInfo();
    return petInfo?.name || 'Your Pet';
  }

  function getPetType() {
    const petInfo = getPetInfo();
    return petInfo?.type?.toLowerCase() || 'dog';
  }

  function getPetEmoji() {
    const petType = getPetType();
    return petType === 'cat' ? '🐱' : '🐕';
  }

  /**
   * Collect quiz data into a flat object suitable for the AI worker.
   * Only uses fields that actually exist in the pets_info cookie:
   *   type, name, birthdate, weight, breed, address.postcode, email
   * Derives computed fields (age, ageStage, sizeBand, isHighRisk) for richer AI context.
   */
  function collectQuizData() {
    const petInfo = getPetInfo();
    if (!petInfo) return null;

    const postcode = petInfo.address?.postcode || '';
    const weight = petInfo.weight || '';
    const birthdate = petInfo.birthdate || '';
    const petType = (petInfo.type || 'dog').toLowerCase();

    // Derive age from birthdate
    let age = 0;
    if (birthdate) {
      const birth = new Date(birthdate);
      if (!isNaN(birth.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        age = Math.max(0, age);
      }
    }

    // Derive life-stage from age + type
    let ageStage = 'adult';
    if (petType === 'dog') {
      if (age <= 1) ageStage = 'puppy';
      else if (age > 7) ageStage = 'senior';
    } else if (petType === 'cat') {
      if (age <= 1) ageStage = 'kitten';
      else if (age > 10) ageStage = 'senior';
    }

    // Derive size band from weight + type
    const weightNum = parseFloat(String(weight).replace(/[^\d.]/g, '')) || 0;
    let sizeBand = '';
    if (petType === 'cat') {
      sizeBand = weightNum <= 4 ? 'small (≤4kg)' : 'medium (>4kg)';
    } else {
      if (weightNum <= 4) sizeBand = 'extra-small (≤4kg)';
      else if (weightNum <= 10) sizeBand = 'small (4-10kg)';
      else if (weightNum <= 25) sizeBand = 'medium (10-25kg)';
      else sizeBand = 'large (>25kg)';
    }

    // Determine heartworm risk from postcode
    const highRisk = isHighRiskPostcode(postcode);

    return {
      name: petInfo.name || '',
      type: petType,
      weight: weight,
      birthdate: birthdate,
      age: age,
      ageStage: ageStage,
      sizeBand: sizeBand,
      breed: petInfo.breed || '',
      postcode: postcode,
      isHighRiskArea: highRisk,
    };
  }

  // ============================================================================
  // PRODUCT DATA FETCHING
  // ============================================================================

  async function fetchProductByHandle(handle) {
    try {
      // Use .js endpoint (has selling_plan_groups needed for subscriptions)
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      const product = await response.json();

      // Also fetch .json to get body_html (product description) for AI personalisation
      try {
        const jsonResp = await fetch(`/products/${handle}.json`);
        if (jsonResp.ok) {
          const jsonData = await jsonResp.json();
          if (jsonData.product && jsonData.product.body_html) {
            product.body_html = jsonData.product.body_html;
          }
        }
      } catch (_) {
        // body_html is optional — AI will work without it
      }

      // Fetch plan IDs in the correct ReCharge-configured order via Liquid section
      try {
        const planResp = await fetch(`/products/${handle}?section_id=pawplan-product-plans`);
        if (planResp.ok) {
          const planHtml = await planResp.text();
          const match = planHtml.match(/<script[^>]+id="pawplan-plans-json"[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            const ordered = JSON.parse(match[1].trim());
            product.__planOrder = ordered.map(p => p.id);
          }
        }
      } catch (_) {
        // Plan order is optional — fallback sort by name will apply
      }

      return product;
    } catch (e) {
      console.error('Error fetching product:', handle, e);
      return null;
    }
  }

  function getRecommendedProductHandle() {
    const urlHandle = getUrlParam('product');
    if (urlHandle) return urlHandle;

    const recoCookie = getCookie('pawplan_recommended_product');
    if (recoCookie) return recoCookie;

    const petInfo = getPetInfo();
    if (petInfo) {
      return calculateProductHandle(petInfo);
    }

    return null;
  }

  function calculateProductHandle(petInfo) {
    const petType = (petInfo.type || '').toLowerCase();
    const weight = parseFloat(petInfo.weight) || 0;
    const postcode = petInfo.address?.postcode || petInfo.postcode || '';

    const isHighRisk = isHighRiskPostcode(postcode);

    if (petType === 'dog') {
      if (isHighRisk) {
        if (weight >= 1.3 && weight <= 2.5) return 'simparica-trio-yellow-puppy';
        if (weight >= 2.6 && weight <= 5) return 'simparica-trio-purple-small-dogs-2-6-5kg-3-pack';
        if (weight >= 5.1 && weight <= 10) return 'simparica-trio-orange-small';
        if (weight >= 10.1 && weight <= 20) return 'simparica-trio-blue-medium';
        if (weight >= 20.1 && weight <= 40) return 'simparica-trio-green-large';
        if (weight >= 40.1 && weight <= 60) return 'simparica-trio-red-xl-3pk';
        if (weight < 1.3) return 'simparica-trio-yellow-puppy';
        return 'simparica-trio-red-xl-3pk';
      } else {
        if (weight < 4) return 'flea-treatment-xsmall-dog';
        if (weight >= 4 && weight <= 10) return 'flea-treatment-small-dog';
        if (weight > 10 && weight <= 25) return 'flea-treatment-medium-dog';
        return 'flea-treatment-large-dog';
      }
    }

    if (petType === 'cat') {
      return weight < 4 ? 'flea-treatment-small-cat' : 'flea-treatment-medium-cat';
    }

    return null;
  }

  function isHighRiskPostcode(postcode) {
    if (!postcode) return false;

    const highRiskPrefixes = [
      '08', '09', '48', '49', '28', '29', '47', '68', '69'
    ];

    const postcodeStr = String(postcode);
    return highRiskPrefixes.some(prefix => postcodeStr.startsWith(prefix));
  }

  // ============================================================================
  // NUGARD / SIMPARICA UPGRADE LOGIC
  // ============================================================================

  function isNugardProduct(handle) {
    if (!handle) return false;
    const petType = getPetType();
    return petType === 'dog' && handle.startsWith('flea-treatment-') && handle.endsWith('-dog');
  }

  function getSimparicaHandleForWeight(weight) {
    const w = parseFloat(weight) || 0;
    if (w < 1.3) return 'simparica-trio-yellow-puppy';
    if (w <= 2.5) return 'simparica-trio-yellow-puppy';
    if (w <= 5) return 'simparica-trio-purple-small-dogs-2-6-5kg-3-pack';
    if (w <= 10) return 'simparica-trio-orange-small';
    if (w <= 20) return 'simparica-trio-blue-medium';
    if (w <= 40) return 'simparica-trio-green-large';
    return 'simparica-trio-red-xl-3pk';
  }

  function renderUpgradeNudge() {
    return `
      <div id="pp-upgrade" class="pp-upgrade-card">
        <p><strong>Travel to bushy or regional areas?</strong><br>
           Upgrade to Simparica for full tick, flea and worm cover
           in one monthly chew.</p>
        <button class="pp-upgrade-btn" type="button">Upgrade to Simparica →</button>
      </div>
    `;
  }

  // Store original Nugard product for revert
  let _nugardOriginalProduct = null;
  let _nugardOriginalHandle = null;

  async function handleUpgradeToSimparica() {
    const petInfo = getPetInfo();
    const weight = petInfo?.weight || 0;
    const simparicaHandle = getSimparicaHandleForWeight(weight);

    // Store original main product for revert
    _nugardOriginalProduct = bundleProducts[0];
    _nugardOriginalHandle = _nugardOriginalProduct.handle;

    // Fetch the Simparica product
    const simparicaProduct = await fetchProductByHandle(simparicaHandle);
    if (!simparicaProduct) {
      showToast('Could not load Simparica product. Please try again.');
      return;
    }

    // Replace main product in bundleProducts
    bundleProducts[0] = simparicaProduct;

    // Re-render main product card
    const container = document.querySelector('.pawplan-bundle-products');
    if (!container) return;

    const mainItem = container.querySelector('.pawplan-product-card--main');
    const upgradeCard = document.getElementById('pp-upgrade');

    // Create new main card HTML
    const newHtml = renderMainProductItem(simparicaProduct, 0);
    const temp = document.createElement('div');
    temp.innerHTML = newHtml;
    const newCard = temp.firstElementChild;

    if (mainItem) mainItem.replaceWith(newCard);

    // Replace upgrade card with revert card
    if (upgradeCard) {
      upgradeCard.innerHTML = `
        <p><strong>Switched to Simparica</strong><br>
           Full tick, flea and worm protection for your dog.</p>
        <button class="pp-revert-btn" type="button">← Revert to Nugard</button>
      `;
    }

    // Re-init all event handlers
    initSubscriptionSelectors();
    initReadMoreButtons();
    initToggleButtons();
    initNugardUpgrade();
    updateTotalPrice();
    updateStickyBanner();

    showToast('Upgraded to Simparica! 🛡️');
  }

  async function handleRevertToNugard() {
    if (!_nugardOriginalProduct) return;

    // Re-fetch to get fresh data
    const nugardProduct = await fetchProductByHandle(_nugardOriginalHandle);
    if (!nugardProduct) {
      showToast('Could not revert. Please try again.');
      return;
    }

    bundleProducts[0] = nugardProduct;

    const container = document.querySelector('.pawplan-bundle-products');
    if (!container) return;

    const mainItem = container.querySelector('.pawplan-product-card--main');

    const newHtml = renderMainProductItem(nugardProduct, 0);
    // Also add upgrade nudge back
    const fullHtml = newHtml + renderUpgradeNudge();
    const temp = document.createElement('div');
    temp.innerHTML = fullHtml;

    // Replace main card and the revert card
    const upgradeCard = document.getElementById('pp-upgrade');
    if (mainItem) {
      mainItem.replaceWith(temp.children[0]);
    }
    if (upgradeCard) {
      upgradeCard.replaceWith(temp.children[0]); // the nudge card is now first child
    }

    _nugardOriginalProduct = null;

    initSubscriptionSelectors();
    initReadMoreButtons();
    initToggleButtons();
    initNugardUpgrade();
    updateTotalPrice();
    updateStickyBanner();

    showToast('Reverted to Nugard');
  }

  function initNugardUpgrade() {
    // Expand/collapse toggle
    document.querySelectorAll('.pp-expand-btn').forEach(btn => {
      btn.addEventListener('click', function () {
        const body = this.nextElementSibling;
        const chevron = this.querySelector('.pp-chevron');
        if (body) body.classList.toggle('open');
        if (chevron) chevron.classList.toggle('open');
      });
    });

    // "Consider an upgrade to all-round cover" inline link — triggers upgrade directly
    document.querySelectorAll('.pp-upgrade-link').forEach(link => {
      link.addEventListener('click', function () {
        handleUpgradeToSimparica();
      });
    });

    // Upgrade button
    document.querySelectorAll('.pp-upgrade-btn').forEach(btn => {
      btn.addEventListener('click', handleUpgradeToSimparica);
    });

    // Revert button
    document.querySelectorAll('.pp-revert-btn, .pp-revert-link').forEach(btn => {
      btn.addEventListener('click', handleRevertToNugard);
    });
  }

  // ============================================================================
  // SUBSCRIPTION PLAN HELPERS
  // ============================================================================

  function get12WeekPlan(product) {
    if (!product.selling_plan_groups) return null;

    for (const group of product.selling_plan_groups) {
      if (group.name && group.name.toLowerCase().includes('12 week')) {
        return group.selling_plans?.[0] || null;
      }
    }
    return null;
  }

  function getAllSellingPlans(product) {
    if (!product.selling_plan_groups) return [];

    const plans = [];
    for (const group of product.selling_plan_groups) {
      for (const plan of (group.selling_plans || [])) {
        plans.push({
          id: plan.id,
          name: plan.name || group.name,
          groupName: group.name,
        });
      }
    }

    if (product.__planOrder && product.__planOrder.length > 0) {
      // Sort by the ReCharge-configured order fetched from Liquid
      const orderMap = new Map(product.__planOrder.map((id, i) => [id, i]));
      plans.sort((a, b) => {
        const posA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
        const posB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
        return posA - posB;
      });
    } else {
      // Fallback: sort by numeric week value in name (4 < 6 < 8 < 12)
      plans.sort((a, b) => (parseInt(a.name) || Infinity) - (parseInt(b.name) || Infinity));
    }

    return plans;
  }

  function getDefaultSellingPlan(product) {
    const plans = getAllSellingPlans(product);
    if (plans.length === 0) return null;

    // Prefer 12-week plan as default
    const twelveWeek = plans.find(p => p.name && p.name.toLowerCase().includes('12 week'));
    return twelveWeek || plans[0];
  }

  // ============================================================================
  // REBUY INTEGRATION
  // ============================================================================

  // ============================================================================
  // CATEGORY HELPERS
  // ============================================================================

  /**
   * Extract a friendly category label from a product's product_type or tags.
   * Uses the CATEGORY_LABELS map with keyword matching.
   */
  function extractCategory(product) {
    const typeStr = (product.product_type || product.type || '').trim().toLowerCase();

    if (typeStr.includes('flea') || typeStr.includes('worm') || typeStr.includes('tick') || typeStr.includes('protection') || typeStr.includes('preventative') || typeStr.includes('preventive') || typeStr.includes('nugard') || typeStr.includes('simparica')) {
      return 'Protection';
    }
    if (typeStr.includes('food') || typeStr.includes('treat') || typeStr.includes('nutrition') || typeStr.includes('diet') || typeStr.includes('meal')) {
      return 'Nutrition';
    }
    if (typeStr.includes('dental') || typeStr.includes('health') || typeStr.includes('care') || typeStr.includes('shampoo') || typeStr.includes('grooming') || typeStr.includes('wash')) {
      return 'Care';
    }
    if (typeStr.includes('joint') || typeStr.includes('calming') || typeStr.includes('supplement') || typeStr.includes('wellbeing') || typeStr.includes('anxiety')) {
      return 'Wellbeing';
    }
    return 'Care';
  }

  /**
   * Returns an emoji icon for a given category label used in section headers.
   */
  function getCategoryIcon(category) {
    if (window.pawplanCategoryIcons && window.pawplanCategoryIcons[category]) {
      // Use the injected image URL from Liquid
      return `<img src="${window.pawplanCategoryIcons[category]}" alt="${category}" style="width: 24px; height: 24px; display: inline-block;">`;
    }
    const iconMap = {
      'Protection': '🛡️',
      'Nutrition': '🥩',
      'Care': '✨',
      'Wellbeing': '🌿'
    };
    return iconMap[category] || '🐾';
  }

  async function fetchRebuyProducts(mainProductId) {
    // Method 1: Use Rebuy's Data Source API (custom ruleset endpoint)
    try {
      const apiUrl = new URL(`https://rebuyengine.com/api/v1/custom/id/${REBUY_DATASOURCE_ID}`);
      apiUrl.searchParams.set('key', REBUY_API_KEY);
      apiUrl.searchParams.set('shopify_product_ids', mainProductId);

      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data.length > 0) {
          return await enrichRebuyProducts(data.data);
        }
      }
    } catch (e) {
      // Data Source API failed, try fallback
    }

    // Method 2: Fallback to Recommended Products API
    try {
      const apiUrl = new URL('https://rebuyengine.com/api/v1/products/recommended');
      apiUrl.searchParams.set('key', REBUY_API_KEY);
      apiUrl.searchParams.set('shopify_product_ids', mainProductId);
      apiUrl.searchParams.set('limit', '4');

      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data.length > 0) {
          return await enrichRebuyProducts(data.data);
        }
      }
    } catch (e) {
      // Recommended API failed
    }

    // All Rebuy methods failed — use curated fallback products
    return await fetchDefaultUpsellProducts();
  }

  // Enrich Rebuy products with full Shopify product data (for selling_plan_groups)
  async function enrichRebuyProducts(rebuyProducts) {
    const enrichedProducts = [];

    for (const product of rebuyProducts) {
      // Rebuy products may not have selling_plan_groups, fetch from Shopify
      const handle = product.handle || product.admin_graphql_api_id?.split('/').pop();
      if (handle) {
        const fullProduct = await fetchProductByHandle(handle);
        if (fullProduct) {
          enrichedProducts.push(fullProduct);
          continue;
        }
      }
      // Fallback: use Rebuy product data as-is
      enrichedProducts.push(product);
    }

    return enrichedProducts;
  }

  async function fetchDefaultUpsellProducts() {
    const petType = getPetType();

    const upsellHandles = petType === 'cat' ? [
      'paw-grocer-freeze-dried-salmon-belly-90g',
      'dental-powder-80g-new',
      'calm-30s'
    ] : [
      'paw-grocer-freeze-dried-beef-liver-90g',
      'dental-powder-80g-new',
      'earth-rated-bags-unscented-60-4-rolls',
      'calm-relax-120g-new'
    ];

    const products = [];
    for (const handle of upsellHandles) {
      const product = await fetchProductByHandle(handle);
      if (product) {
        products.push(product);
      }
    }

    return products;
  }

  // ============================================================================
  // UI RENDERING
  // ============================================================================

  function updateHeroContent() {
    const petName = getPetName();
    const petNameEl = document.querySelector('.pawplan-results__pet-name');
    if (petNameEl) {
      petNameEl.textContent = petName;
    }

    const messageEl = document.querySelector('.pawplan-personalized-box__text');
    if (messageEl) {
      const petEmoji = getPetEmoji();
      messageEl.innerHTML = `
        ${petEmoji} <span class="pawplan-personalized-box__highlight">${petName}'s</span> personalized wellness plan is ready!
      `;
    }
  }

  function renderMainProductItem(product, index = 0) {
    const variant = product.variants?.[0];
    const variantId = variant?.id || product.id;
    const price = variant?.price || product.price;
    const comparePrice = variant?.compare_at_price || product.compare_at_price;
    const imageUrl = product.featured_image || product.images?.[0] || '';
    const description = stripHtml(product.description);
    const shortDesc = truncateText(description, 100);
    const hasLongDesc = description.length > 100;
    const savings = comparePrice && comparePrice > price ? Math.round((1 - price / comparePrice) * 100) : 0;
    const category = extractCategory(product);

    const sellingPlans = getAllSellingPlans(product);
    const hasSubscription = sellingPlans.length > 0;
    const defaultPlanId = ''; // Default to one-time purchase

    let subscriptionHtml = '';
    if (hasSubscription) {
      subscriptionHtml = `
        <div class="pawplan-subscription-selector">
          <label class="pawplan-subscription-label">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
            DELIVERY FREQUENCY - Save time, switch to auto-delivery anytime!
          </label>
          <select class="pawplan-subscription-dropdown" data-variant-id="${variantId}">
            ${sellingPlans.map(plan => `
              <option value="${plan.id}" ${plan.id === defaultPlanId ? 'selected' : ''}>${plan.name}</option>
            `).join('')}
            <option value="" ${defaultPlanId === '' ? 'selected' : ''}>One-time purchase</option>
          </select>
        </div>
      `;
    }

    const isNugard = isNugardProduct(product.handle);
    const petName = getPetName();

    let tickWarningHtml = '';
    if (isNugard) {
      tickWarningHtml = `
        <div class="pp-tick-warning">
          <div class="pp-warn-icon">
            <svg viewBox="0 0 8 8" width="8" height="8" fill="none">
              <path d="M4 1.5v3M4 6v.5" stroke="#fff" stroke-width="1.2" stroke-linecap="round"/>
            </svg>
          </div>
          <p>Tick protection not included — Nugard suits low-risk areas.
             If you travel to bushland, <button class="pp-upgrade-link" type="button">click here to upgrade to all-round cover</button>.
          </p>
        </div>
      `;
    } else if (product.handle.includes('simparica')) {
      tickWarningHtml = `
        <div class="pp-tick-warning">
          <div class="pp-warn-icon" style="background: var(--pp-green);">
            <svg viewBox="0 0 8 8" width="8" height="8" fill="none">
              <path d="M1.5 4l2 2 3-4" stroke="#fff" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p>Upgraded to all-round cover.
             <button class="pp-revert-link pp-upgrade-link" type="button">Click here to revert to Nugard</button>.
          </p>
        </div>
      `;
    }


    let expandHtml = '';
    if (isNugard) {
      expandHtml = `
        <div class="pp-expand">
          <button class="pp-expand-btn" type="button">
            <span>Why did we recommend this?</span>
            <svg class="pp-chevron" width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M2.5 4.5l4 4 4-4" stroke="#4a7a4a" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="pp-expand-body">
            Your postcode falls within a low tick risk zone. Nugard targets the parasites
            most likely to affect your dog — fleas and intestinal worms — without the
            added cost of tick protection you're unlikely to need.
          </div>
        </div>
      `;
    }

    const savingsBadge = savings > 0 ? `<span class="pp-card__savings">Save ${savings}%</span>` : '';

    return `
      <div class="pawplan-product-card pawplan-product-card--main pawplan-slide-up"
           data-variant-id="${variantId}"
           data-product-handle="${product.handle}"
           data-selling-plan-id="${defaultPlanId}"
           data-requires-selling-plan="${product.requires_selling_plan || false}"
           data-price="${price}"
           data-excluded="false"
           style="animation-delay: ${index * 0.08}s">


        <div class="pp-card__image">
          <a href="/products/${product.handle}" target="_blank">
            <img src="${imageUrl}" alt="${product.title}" loading="lazy">
          </a>
        </div>

        <div class="pp-card__body">
          <h3 class="pp-card__name">
            <a href="/products/${product.handle}" target="_blank">${product.title}</a>
          </h3>
          <div class="pawplan-product-rationale--loading" data-rationale-for="${product.handle}"></div>
          <p class="pp-card__desc" data-full-text="${escapeHtml(description)}">
            ${shortDesc}${hasLongDesc ? ' <button class="pawplan-read-more" type="button">Read more</button>' : ''}
          </p>
          ${tickWarningHtml}
          ${expandHtml}
        </div>

        ${subscriptionHtml ? `<div style="padding: 0 15px 12px 15px;">${subscriptionHtml}</div>` : ''}
        <div class="pp-card__footer">
          <div class="pp-card__price">
            <span class="pp-card__price-current">${formatPrice(price)}</span>
            ${comparePrice && comparePrice > price ? `<span class="pp-card__price-original">${formatPrice(comparePrice)}</span>` : ''}
            ${savingsBadge}
          </div>
          <button type="button" class="pp-toggle-btn pp-toggle-btn--active" data-action="toggle">
            <svg class="pp-toggle-btn__icon-check" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <svg class="pp-toggle-btn__icon-x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            <span class="pp-toggle-btn__label-in">In Plan</span>
            <span class="pp-toggle-btn__label-out">Remove</span>
          </button>
        </div>
      </div>
    `;
  }

  function escapeHtml(text) {
    if (!text) return '';
    return text.replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function renderUpsellProductItem(product, index = 0) {
    const variant = product.variants?.[0];
    const variantId = variant?.id || product.id;
    const price = variant?.price || product.price;
    const comparePrice = variant?.compare_at_price || product.compare_at_price;
    const imageUrl = product.featured_image || product.images?.[0] || '';
    const description = stripHtml(product.description);
    const shortDesc = truncateText(description, 80);
    const hasLongDesc = description.length > 80;
    const savings = comparePrice && comparePrice > price ? Math.round((1 - price / comparePrice) * 100) : 0;
    const category = extractCategory(product);

    const sellingPlans = getAllSellingPlans(product);
    const hasSubscription = sellingPlans.length > 0;
    const defaultPlanId = ''; // Default to one-time purchase

    let subscriptionHtml = '';
    if (hasSubscription) {
      subscriptionHtml = `
        <div class="pawplan-subscription-selector">
          <label class="pawplan-subscription-label">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
            DELIVERY FREQUENCY - Save time, switch to auto-delivery anytime!
          </label>
          <select class="pawplan-subscription-dropdown" data-variant-id="${variantId}">
            ${sellingPlans.map(plan => `
              <option value="${plan.id}" ${plan.id === defaultPlanId ? 'selected' : ''}>${plan.name}</option>
            `).join('')}
            <option value="" ${defaultPlanId === '' ? 'selected' : ''}>One-time purchase</option>
          </select>
        </div>
      `;
    }

    const savingsBadge = savings > 0 ? `<span class="pp-card__savings">${savings}% off</span>` : '';

    return `
      <div class="pawplan-product-card pawplan-product-card--addon pawplan-slide-up"
           data-variant-id="${variantId}"
           data-product-handle="${product.handle}"
           data-selling-plan-id="${defaultPlanId}"
           data-requires-selling-plan="${product.requires_selling_plan || false}"
           data-price="${price}"
           data-excluded="false"
           data-category="${category}"
           style="animation-delay: ${index * 0.06}s">

        <div class="pp-card__image">
          <a href="/products/${product.handle}" target="_blank">
            <img src="${imageUrl}" alt="${product.title}" loading="lazy">
          </a>
        </div>

        <div class="pp-card__body">
          <h3 class="pp-card__name">
            <a href="/products/${product.handle}" target="_blank">${product.title}</a>
          </h3>
          <div class="pawplan-product-rationale--loading" data-rationale-for="${product.handle}"></div>
          <p class="pp-card__desc" data-full-text="${escapeHtml(description)}">
            ${shortDesc}${hasLongDesc ? ' <button class="pawplan-read-more" type="button">Read more</button>' : ''}
          </p>
        </div>

        ${subscriptionHtml ? `<div style="padding: 0 15px 12px 15px;">${subscriptionHtml}</div>` : ''}
        <div class="pp-card__footer">
          <div class="pp-card__price">
            <span class="pp-card__price-current">${formatPrice(price)}</span>
            ${comparePrice && comparePrice > price ? `<span class="pp-card__price-original">${formatPrice(comparePrice)}</span>` : ''}
            ${savingsBadge}
          </div>
          <button type="button" class="pp-toggle-btn pp-toggle-btn--active" data-action="toggle">
            <svg class="pp-toggle-btn__icon-check" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <svg class="pp-toggle-btn__icon-x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            <span class="pp-toggle-btn__label-in">In Plan</span>
            <span class="pp-toggle-btn__label-out">Remove</span>
          </button>
        </div>
      </div>
    `;
  }

  function stripHtml(html) {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }

  function truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength).trim() + '...';
  }

  function updateTotalPrice() {
    const productItems = document.querySelectorAll('.pawplan-product-card');
    let total = 0;
    let count = 0;
    let shortestFrequency = Infinity;

    productItems.forEach(item => {
      if (item.dataset.excluded === 'true') return;

      const price = parseInt(item.dataset.price) || 0;
      total += price;
      count++;

      const dropdown = item.querySelector('.pawplan-subscription-dropdown');
      if (dropdown && dropdown.value) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        if (selectedOption) {
          const weekNum = parseInt(selectedOption.textContent) || Infinity;
          if (weekNum < shortestFrequency) shortestFrequency = weekNum;
        }
      }
    });

    // Update total price display
    const totalEl = document.querySelector('.pawplan-bundle-total__price');
    if (totalEl) {
      totalEl.textContent = formatPrice(total);
    }

    // Update plan card header count
    const countEl = document.querySelector('.pawplan-bundle-card__count');
    if (countEl) {
      countEl.textContent = `${count} item${count !== 1 ? 's' : ''} in plan`;
    }

    // Update sticky banner summary strip
    const summaryEl = document.querySelector('.pawplan-footer-summary');
    if (summaryEl) {
      const petName = getPetName();
      const freqText = shortestFrequency < Infinity ? `${shortestFrequency} weeks` : 'varies';
      summaryEl.textContent = `${petName}'s plan · ${count} item${count !== 1 ? 's' : ''} · deliveries from ${freqText}`;
    }

    // Update footer item count badge
    const footerCountEl = document.querySelector('.pawplan-footer-count');
    if (footerCountEl) {
      footerCountEl.textContent = `${count} item${count !== 1 ? 's' : ''}`;
    }

    // Update Start my plan button disabled state
    const startBtn = document.querySelector('.pawplan-start-plan-btn');
    if (startBtn) {
      startBtn.disabled = count === 0;
      if (count === 0) {
        startBtn.classList.add('pawplan-start-plan-btn--disabled');
      } else {
        startBtn.classList.remove('pawplan-start-plan-btn--disabled');
      }
    }
  }

  function updateStickyBanner() {
    updateTotalPrice();
  }

  function renderEmptyState() {
    // Hide full-screen loader first
    hideLoading();

    const container = document.querySelector('.pawplan-bundle-products');
    if (container) {
      container.innerHTML = `
        <div class="pawplan-empty-state">
          <div class="pawplan-empty-state__icon">🐾</div>
          <h3 class="pawplan-empty-state__title">Let's Find Your Perfect Plan</h3>
          <p class="pawplan-empty-state__text">Take our quick quiz to get personalized product recommendations for your pet.</p>
          <button class="pawplan-empty-state__btn" onclick="document.querySelector('[data-popup-target]')?.click()">
            Start Quiz
          </button>
        </div>
      `;
    }
  }

  function showLoading() {
    // Create full-screen loading overlay
    const petName = getPetName();
    const loader = document.createElement('div');
    loader.className = 'pawplan-fullscreen-loader';
    loader.id = 'pawplan-fullscreen-loader';
    loader.innerHTML = `
      <div class="pawplan-fullscreen-loader__content">
        <div class="pawplan-fullscreen-loader__paw">🐾</div>
        <div class="pawplan-fullscreen-loader__spinner"></div>
        <h2 class="pawplan-fullscreen-loader__title">Creating Your PawPlan</h2>
        <p class="pawplan-fullscreen-loader__subtitle" id="loader-default-subtitle">
          Finding the perfect products for <span class="pawplan-fullscreen-loader__pet-name">${petName}</span>...
        </p>
        <p class="pawplan-fullscreen-loader__ai-text" id="loader-ai-text"></p>
      </div>
    `;
    document.body.appendChild(loader);

    // Also show loading in products container as fallback
    const container = document.querySelector('.pawplan-bundle-products');
    if (container) {
      container.innerHTML = '<div class="pawplan-loading-spinner"></div>';
    }
  }

  function hideLoading() {
    const loader = document.getElementById('pawplan-fullscreen-loader');
    if (loader) {
      loader.classList.add('hidden');
      // Remove from DOM after transition
      setTimeout(() => {
        if (loader.parentNode) {
          loader.parentNode.removeChild(loader);
        }
      }, 400);
    }
  }

  /**
   * Typewriter effect: types AI text character-by-character inside the preloader.
   * Returns a Promise that resolves when typing is finished.
   * @param {string} text - The text to type out
   * @param {number} speed - Milliseconds between characters (default 25)
   */
  function typeTextInLoader(text, speed = 25) {
    return new Promise((resolve) => {
      const aiTextEl = document.getElementById('loader-ai-text');
      const defaultSubEl = document.getElementById('loader-default-subtitle');

      if (!aiTextEl) {
        resolve();
        return;
      }

      // Hide default subtitle, show AI text area
      if (defaultSubEl) defaultSubEl.style.display = 'none';
      aiTextEl.style.display = 'block';

      // Also hide the spinner since we're now showing the AI summary
      const spinner = document.querySelector('.pawplan-fullscreen-loader__spinner');
      if (spinner) spinner.style.display = 'none';

      // Update the title to indicate the plan is ready
      const titleEl = document.querySelector('.pawplan-fullscreen-loader__title');
      if (titleEl) titleEl.textContent = 'Your PawPlan is Ready!';

      let index = 0;
      const cursor = document.createElement('span');
      cursor.className = 'pawplan-typing-cursor';
      cursor.textContent = '|';
      aiTextEl.appendChild(cursor);

      function typeChar() {
        if (index < text.length) {
          // Insert character before cursor
          cursor.before(document.createTextNode(text.charAt(index)));
          index++;
          setTimeout(typeChar, speed);
        } else {
          // Typing done — blink cursor briefly then remove
          setTimeout(() => {
            cursor.classList.add('pawplan-typing-cursor--done');
            setTimeout(resolve, 800);
          }, 400);
        }
      }

      typeChar();
    });
  }

  // ============================================================================
  // SUBSCRIPTION SELECTOR HANDLERS
  // ============================================================================

  function initSubscriptionSelectors() {
    document.querySelectorAll('.pawplan-subscription-dropdown').forEach(select => {
      select.addEventListener('change', function () {
        const productCard = this.closest('.pawplan-product-card');
        if (productCard) {
          productCard.dataset.sellingPlanId = this.value;
          updateTotalPrice();
        }
      });
    });
  }

  // ============================================================================
  // READ MORE FUNCTIONALITY
  // ============================================================================

  function initReadMoreButtons() {
    document.querySelectorAll('.pawplan-read-more').forEach(button => {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        const descEl = this.closest('.pp-card__desc');
        if (!descEl) return;

        const fullText = descEl.dataset.fullText || '';
        const isExpanded = descEl.classList.contains('expanded');

        if (isExpanded) {
          // Collapse
          descEl.classList.remove('expanded');
          descEl.innerHTML = truncateText(fullText, 80) + ' <button class="pawplan-read-more" type="button">Read more</button>';
        } else {
          // Expand
          descEl.classList.add('expanded');
          descEl.innerHTML = fullText + ' <button class="pawplan-read-more" type="button">Read less</button>';
        }

        // Re-bind event handlers
        initReadMoreButtons();
      });
    });
  }

  // ============================================================================
  // NO THANKS / EXCLUDE FUNCTIONALITY
  // ============================================================================

  function initToggleButtons() {
    document.querySelectorAll('.pp-toggle-btn[data-action="toggle"]').forEach(button => {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        const card = this.closest('.pawplan-product-card');
        if (!card) return;

        const isExcluded = card.dataset.excluded === 'true';

        if (isExcluded) {
          // Re-include in plan
          card.dataset.excluded = 'false';
          card.classList.remove('pawplan-product-card--excluded');
          this.classList.remove('pp-toggle-btn--inactive');
          this.classList.add('pp-toggle-btn--active');
          showToast('Added to your plan! ✓');
        } else {
          // Exclude from plan
          card.dataset.excluded = 'true';
          card.classList.add('pawplan-product-card--excluded');
          this.classList.remove('pp-toggle-btn--active');
          this.classList.add('pp-toggle-btn--inactive');
          showToast('Removed from your plan');
        }

        updateTotalPrice();
      });
    });
  }

  // ============================================================================
  // CART FUNCTIONALITY
  // ============================================================================

  async function addAllToCart() {
    const button = document.querySelector('.pawplan-add-all-btn');
    const productCards = document.querySelectorAll('.pawplan-product-card');

    if (productCards.length === 0) {
      showToast('No products to add');
      return;
    }

    // Show loading state
    const originalContent = button.innerHTML;
    button.classList.add('pawplan-add-all-btn--loading');
    button.disabled = true;
    button.innerHTML = '<span class="pawplan-add-all-btn__spinner"></span> Adding to cart...';

    // Get pet info for line item properties
    const petInfo = getPetInfo();
    const petProperties = petInfo ? {
      '_pet_name': petInfo.name || '',
      '_pet_type': petInfo.type || '',
      '_pet_weight': petInfo.weight || '',
      '_pet_birthdate': petInfo.birthdate || '',
      '_bundle': 'true'
    } : { '_bundle': 'true' };

    // Build items array
    const items = [];
    let isFirst = true;

    productCards.forEach(item => {
      if (item.dataset.excluded === 'true') return;

      const variantId = parseInt(item.dataset.variantId);
      const sellingPlanId = item.dataset.sellingPlanId;
      const requiresSellingPlan = item.dataset.requiresSellingPlan === 'true';

      const cartItem = {
        id: variantId,
        quantity: 1
      };

      // Add selling plan if selected or required
      if (sellingPlanId && sellingPlanId !== '') {
        cartItem.selling_plan = parseInt(sellingPlanId);
      } else if (requiresSellingPlan) {
        // Product requires subscription but none selected - this shouldn't happen for main product
        console.warn('Product requires selling plan but none set:', item.dataset.productHandle);
      }

      // Add pet properties to first item only
      if (isFirst) {
        cartItem.properties = petProperties;
        isFirst = false;
      }

      items.push(cartItem);
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cart add error:', errorData);
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const cart = await response.json();

      // Success!
      showToast(`${items.length} item${items.length > 1 ? 's' : ''} added to cart! 🎉`);

      // Update cart count in header
      updateCartCount();

      // Open cart drawer after a short delay
      setTimeout(() => {
        openCartDrawer();
      }, 800);

    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast(error.message || 'Error adding products. Please try again.');
    } finally {
      // Reset button
      button.classList.remove('pawplan-add-all-btn--loading');
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  let isUpdatingCart = false;
  function updateCartCount() {
    if (isUpdatingCart) return;
    isUpdatingCart = true;
    // Fetch the cart-icon-bubble section to update the count
    fetch(`${window.location.origin}/?section_id=cart-icon-bubble`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBubbleContent = doc.querySelector('.shopify-section');

        if (newBubbleContent) {
          // Update cart icon elements — exclude PawPlan footer links
          document.querySelectorAll('a[href="/cart"]:not(.pawplan-review-link):not(.pawplan-footer-links a)').forEach(cartLink => {
            cartLink.innerHTML = newBubbleContent.innerHTML;
          });
        }
      })
      .catch(() => {
        // Fallback: manually update count
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const countElements = document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden), [data-cart-count]');
            countElements.forEach(el => {
              el.textContent = cart.item_count;
            });

            // If cart was empty and bubble didn't exist, create it
            const cartLinks = document.querySelectorAll('a[href="/cart"]');
            cartLinks.forEach(link => {
              let bubble = link.querySelector('.cart-count-bubble');
              if (!bubble && cart.item_count > 0) {
                bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                bubble.innerHTML = `<span aria-hidden="true">${cart.item_count}</span>`;
                link.appendChild(bubble);
              }
            });
          });
      })
      .finally(() => {
        // Dispatch cart updated events
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const ppBubble = document.querySelector('.pawplan-cart-count');
            if (ppBubble) {
              ppBubble.textContent = cart.item_count;
              ppBubble.style.display = cart.item_count > 0 ? 'flex' : 'none';
            }
            if (window.Shopify && window.Shopify.theme) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            }
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: cart
            }));
          })
          .finally(() => {
            isUpdatingCart = false;
          });
      });
  }

  function openCartDrawer() {
    // Try Rebuy Smart Cart first
    try {
      if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
        window.Rebuy.SmartCart.show();
        return;
      }
    } catch (e) { }

    // Try cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    // Try clicking cart icon
    const cartIcon = document.querySelector('[href="/cart"], .header__icon--cart, [data-cart-toggle]');
    if (cartIcon) {
      cartIcon.click();
      return;
    }

    // Fallback: redirect to cart page
    window.location.href = '/cart';
  }

  // ============================================================================
  // AI PERSONALISATION
  // ============================================================================

  /**
   * Call the Cloudflare Worker proxy which forwards to Anthropic.
   * Returns { heroSummary, rationales } or null on failure.
   */
  async function fetchAICopy(quizData, products) {
    try {
      const payload = {
        quizData,
        products: products.map(p => {
          // Collect selling plan names for AI frequency recommendation
          const plans = getAllSellingPlans(p);
          return {
            title: p.title,
            handle: p.handle,
            description: (p.body_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 500),
            sellingPlans: plans.map(sp => sp.name),
          };
        }),
      };

      const response = await fetch(AI_WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.warn('AI worker returned', response.status);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.warn('AI personalisation fetch failed:', err);
      return null;
    }
  }

  /**
   * Inject AI-generated copy into the page.
   * - heroSummary → replaces the static subtitle in the hero
   * - rationales  → injects below each product title
   */
  function injectAICopy(aiData) {
    if (!aiData) return;

    // --- Hero subtitle ---
    if (aiData.heroSummary) {
      const staticSub = document.querySelector('.pawplan-results__subtitle');
      const aiSub = document.getElementById('pawplan-ai-subtitle');
      const shimmer = document.getElementById('pawplan-ai-shimmer');

      if (staticSub) staticSub.style.display = 'none';
      if (shimmer) shimmer.style.display = 'none';
      if (aiSub) {
        aiSub.textContent = aiData.heroSummary;
        aiSub.style.display = 'block';
      }
    }

    // --- Per-product rationales ---
    if (aiData.rationales && typeof aiData.rationales === 'object') {
      document.querySelectorAll('[data-rationale-for]').forEach(placeholder => {
        const handle = placeholder.dataset.rationaleFor;
        const text = aiData.rationales[handle];

        if (text) {
          const rationaleEl = document.createElement('p');
          rationaleEl.className = 'pawplan-product-rationale';
          rationaleEl.textContent = text;
          // Set as data attribute on the product item for accessibility
          const productCard = placeholder.closest('.pawplan-product-card');
          if (productCard) {
            productCard.dataset.aiRationale = text;
          }
          placeholder.replaceWith(rationaleEl);
        } else {
          // No rationale for this product — remove the loading shimmer
          placeholder.remove();
        }
      });
    }

    // --- AI-recommended delivery frequencies ---
    // Disabled to strictly enforce One-Time Purchase as the default for all products
    /*
    if (aiData.deliveryFrequencies && typeof aiData.deliveryFrequencies === 'object') {
      document.querySelectorAll('.pawplan-product-card').forEach(item => {
        const handle = item.dataset.productHandle;
        const recommended = aiData.deliveryFrequencies[handle];
        if (!recommended) return;

        const dropdown = item.querySelector('.pawplan-subscription-dropdown');
        if (!dropdown) return;

        // Find the option that matches the AI recommendation
        for (let i = 0; i < dropdown.options.length; i++) {
          if (dropdown.options[i].textContent.trim().toLowerCase() === recommended.toLowerCase()) {
            dropdown.selectedIndex = i;
            item.dataset.sellingPlanId = dropdown.options[i].value;
            break;
          }
        }
      });

      // Recalculate totals with new selections
      updateTotalPrice();
    }
    */
  }

  /**
   * Remove all AI loading shimmer placeholders (called on failure/timeout).
   */
  function removeAIShimmers() {
    const shimmer = document.getElementById('pawplan-ai-shimmer');
    if (shimmer) shimmer.style.display = 'none';

    document.querySelectorAll('.pawplan-product-rationale--loading').forEach(el => el.remove());
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  async function initBundlePage() {
    const container = document.querySelector('.pawplan-results');
    if (!container) return;

    // Listen for cart changes to update bubble via AJAX
    document.addEventListener('cart:updated', updateCartCount);
    document.addEventListener('cart:refresh', updateCartCount);
    document.addEventListener('rebuy.cart.change', updateCartCount);
    document.addEventListener('rebuy:cart.change', updateCartCount);

    // Intercept network requests to reliably catch all cart updates from third-party apps
    if (!window.__pawplanCartObserver) {
      window.__pawplanCartObserver = true;
      const origFetch = window.fetch;
      window.fetch = async function() {
        const response = await origFetch.apply(this, arguments);
        const url = arguments[0];
        if (typeof url === 'string' && url.includes('/cart') && !url.includes('cart.js')) {
          setTimeout(updateCartCount, 300);
        }
        return response;
      };
      
      if (window.XMLHttpRequest) {
        const origXHR = window.XMLHttpRequest.prototype.send;
        window.XMLHttpRequest.prototype.send = function() {
          this.addEventListener('load', function() {
            if (this.responseURL && this.responseURL.includes('/cart') && !this.responseURL.includes('cart.js')) {
              setTimeout(updateCartCount, 300);
            }
          });
          return origXHR.apply(this, arguments);
        };
      }
    }

    // Update hero with pet name
    updateHeroContent();
    updateCartCount();

    // Show loading state
    showLoading();

    // Get recommended product
    const productHandle = getRecommendedProductHandle();

    if (!productHandle) {
      renderEmptyState();
      return;
    }

    // Fetch main product
    const mainProduct = await fetchProductByHandle(productHandle);

    if (!mainProduct) {
      renderEmptyState();
      return;
    }

    // Store main product
    bundleProducts = [mainProduct];

    // Fetch upsell products from Rebuy data source
    const upsellProducts = await fetchRebuyProducts(mainProduct.id);

    // Store all products
    upsellProducts.forEach(p => {
      if (p.handle !== mainProduct.handle && p.id !== mainProduct.id) {
        bundleProducts.push(p);
      }
    });

    // Render products grouped by category
    const productsContainer = document.querySelector('.pawplan-bundle-products');
    if (productsContainer) {
      let html = '';

      const categoriesList = ['Protection', 'Nutrition', 'Care', 'Wellbeing'];
      const groupedProducts = {};
      categoriesList.forEach(c => groupedProducts[c] = []);

      bundleProducts.forEach(product => {
        const cat = extractCategory(product);
        if (groupedProducts[cat]) {
          groupedProducts[cat].push(product);
        } else {
          if (!groupedProducts['Care']) groupedProducts['Care'] = [];
          groupedProducts['Care'].push(product);
        }
      });

      let productIndex = 0;

      html += `<div class="pawplan-addon-grid">`;

      categoriesList.forEach(category => {
        if (groupedProducts[category] && groupedProducts[category].length > 0) {
          groupedProducts[category].forEach(product => {
            html += `<div class="pawplan-product-wrapper">`;

            html += `<div class="pawplan-category-header" style="margin-top:0 !important; padding-top:0 !important; border:none !important; padding-bottom: 12px !important; justify-content: center !important;">
              <span class="pawplan-category-header__icon">${getCategoryIcon(category)}</span>
              <span class="pawplan-category-header__label" style="flex: 0 !important;">${category}</span>
            </div>`;

            if (product.id === mainProduct.id) {
              html += renderMainProductItem(product, productIndex);
            } else {
              html += renderUpsellProductItem(product, productIndex);
            }

            html += `</div>`;
            productIndex++;
          });
        }
      });

      html += `</div>`;

      productsContainer.innerHTML = html;

      initSubscriptionSelectors();
      initReadMoreButtons();
      initToggleButtons();
      initNugardUpgrade();
      updateTotalPrice();

      // ----- AI personalisation (blocking — shown in preloader) -----
      const quizData = collectQuizData();
      let aiData = null;

      if (quizData) {
        try {
          aiData = await fetchAICopy(quizData, bundleProducts);
        } catch (e) {
          aiData = null;
        }
      }

      if (aiData && aiData.heroSummary) {
        await typeTextInLoader(aiData.heroSummary);
        injectAICopy(aiData);
      } else {
        removeAIShimmers();
      }

      hideLoading();
    }

    // Bind sticky footer buttons
    initStickyFooter();
  }

  // ============================================================================
  // STICKY FOOTER BUTTON HANDLERS
  // ============================================================================

  function initStickyFooter() {
    // "Start my plan" → add all to cart then go to checkout
    const startBtn = document.querySelector('.pawplan-start-plan-btn');
    if (startBtn) {
      startBtn.addEventListener('click', startMyPlan);
    }

    // "Add another pet" → add plan to cart first, then launch quiz
    const addPetBtn = document.querySelector('.pawplan-add-pet-btn');
    if (addPetBtn) {
      addPetBtn.addEventListener('click', async function () {
        try {
          await addPlanToCart();
          showToast('Plan added to cart! Starting quiz for your next pet...');
          setTimeout(() => {
            const quizTrigger = document.querySelector('[data-popup-target]');
            if (quizTrigger) {
              quizTrigger.click();
            } else {
              window.location.href = '/pages/quiz';
            }
          }, 600);
        } catch (err) {
          showToast(err.message || 'Error adding to cart. Please try again.');
        }
      });
    }

    // "Review your plan" → add plan to cart then open side cart
    const reviewLink = document.querySelector('.pawplan-review-link');
    if (reviewLink) {
      reviewLink.addEventListener('click', async function (e) {
        e.preventDefault();
        try {
          await addPlanToCart();
          updateCartCount();
          setTimeout(() => {
            openCartDrawer();
          }, 300);
        } catch (err) {
          showToast(err.message || 'Error adding to cart. Please try again.');
        }
      });
    }

    // "Edit your plan" → scroll to top
    const editLink = document.querySelector('.pawplan-edit-link');
    if (editLink) {
      editLink.addEventListener('click', function (e) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  /**
   * Shared helper: builds cart items from current plan and adds them via /cart/add.js
   * Returns a promise that resolves on success, rejects on failure.
   */
  async function addPlanToCart() {
    const productCards = document.querySelectorAll('.pawplan-product-card');

    const petInfo = getPetInfo();
    const petProperties = petInfo ? {
      '_pet_name': petInfo.name || '',
      '_pet_type': petInfo.type || '',
      '_pet_weight': petInfo.weight || '',
      '_pet_birthdate': petInfo.birthdate || '',
      '_bundle': 'true'
    } : { '_bundle': 'true' };

    const items = [];
    let isFirst = true;

    productCards.forEach(item => {
      if (item.dataset.excluded === 'true') return;

      const variantId = parseInt(item.dataset.variantId);
      const sellingPlanId = item.dataset.sellingPlanId;

      const cartItem = { id: variantId, quantity: 1 };

      if (sellingPlanId && sellingPlanId !== '') {
        cartItem.selling_plan = parseInt(sellingPlanId);
      }

      if (isFirst) {
        cartItem.properties = petProperties;
        isFirst = false;
      }

      items.push(cartItem);
    });

    if (items.length === 0) {
      throw new Error('No products in your plan');
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || 'Failed to add to cart');
    }

    return await response.json();
  }

  async function startMyPlan() {
    const button = document.querySelector('.pawplan-start-plan-btn');
    if (!button || button.disabled) return;

    const productCards = document.querySelectorAll('.pawplan-product-card');
    if (productCards.length === 0) {
      showToast('No products to add');
      return;
    }

    // Show loading state
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<span class="pawplan-add-all-btn__spinner"></span> Processing...';

    // Get pet info for line item properties
    const petInfo = getPetInfo();
    const petProperties = petInfo ? {
      '_pet_name': petInfo.name || '',
      '_pet_type': petInfo.type || '',
      '_pet_weight': petInfo.weight || '',
      '_pet_birthdate': petInfo.birthdate || '',
      '_bundle': 'true'
    } : { '_bundle': 'true' };

    // Build items array
    const items = [];
    let isFirst = true;

    productCards.forEach(item => {
      if (item.dataset.excluded === 'true') return;

      const variantId = parseInt(item.dataset.variantId);
      const sellingPlanId = item.dataset.sellingPlanId;

      const cartItem = {
        id: variantId,
        quantity: 1
      };

      if (sellingPlanId && sellingPlanId !== '') {
        cartItem.selling_plan = parseInt(sellingPlanId);
      }

      if (isFirst) {
        cartItem.properties = petProperties;
        isFirst = false;
      }

      items.push(cartItem);
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      // Success — redirect straight to checkout
      window.location.href = '/checkout';

    } catch (error) {
      console.error('Error starting plan:', error);
      showToast(error.message || 'Error adding products. Please try again.');
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundlePage);
  } else {
    initBundlePage();
  }

  // Expose for external use
  window.PawPlanBundle = {
    init: initBundlePage,
    startMyPlan,
    addAllToCart,
    getPetInfo,
    showToast
  };

})();
